import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { indicatorSetApi } from '../../services/api';
import type { IndicatorSetListItem } from '../../types';

interface IndicatorSetPickerProps {
  value: number | null;
  onChange: (indicatorSetId: number | null) => void;
  label?: string;
  helperText?: string;
  required?: boolean;
  error?: boolean;
}

export const IndicatorSetPicker: React.FC<IndicatorSetPickerProps> = ({
  value,
  onChange,
  label = 'Indicator Set',
  helperText,
  required = false,
  error = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [indicatorSets, setIndicatorSets] = useState<IndicatorSetListItem[]>([]);
  const [filteredSets, setFilteredSets] = useState<IndicatorSetListItem[]>([]);

  // Fetch indicator sets
  const { data, isLoading, error: fetchError } = useQuery({
    queryKey: ['indicator-sets-picker'],
    queryFn: () => indicatorSetApi.list({ is_active: true }),
  });

  // Fetch categories for filter
  const { data: categories } = useQuery({
    queryKey: ['indicator-set-categories'],
    queryFn: indicatorSetApi.getCategories,
  });

  // Update local state when data is fetched
  useEffect(() => {
    if (data?.results) {
      setIndicatorSets(data.results);
      setFilteredSets(data.results);
    }
  }, [data]);

  // Filter indicator sets based on search and category
  useEffect(() => {
    let filtered = indicatorSets;

    if (searchQuery) {
      filtered = filtered.filter(
        (set) =>
          set.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          set.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          set.indicator_names.some((name) =>
            name.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter((set) => set.category === selectedCategory);
    }

    setFilteredSets(filtered);
  }, [indicatorSets, searchQuery, selectedCategory]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (fetchError) {
    return <Alert severity="error">Failed to load indicator sets</Alert>;
  }

  const selectedSet = indicatorSets.find((set) => set.id === value);

  return (
    <Box>
      {/* Search and Category Filter */}
      <Box display="flex" gap={2} mb={2}>
        <TextField
          size="small"
          placeholder="Search indicator sets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        {categories && categories.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Category"
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Indicator Set Selector */}
      <FormControl fullWidth required={required} error={error}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          label={label}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {filteredSets.map((set) => (
            <MenuItem key={set.id} value={set.id}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <TimelineIcon fontSize="small" color="primary" />
                <Box flexGrow={1}>
                  <Typography variant="body2" fontWeight="medium">
                    {set.name}
                  </Typography>
                  {set.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {set.description}
                    </Typography>
                  )}
                  <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                    {set.indicator_names.slice(0, 3).map((name, idx) => (
                      <Chip
                        key={idx}
                        label={name}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    ))}
                    {set.indicator_names.length > 3 && (
                      <Chip
                        label={`+${set.indicator_names.length - 3}`}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
            {helperText}
          </Typography>
        )}
      </FormControl>

      {/* Selected Set Summary */}
      {selectedSet && (
        <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="subtitle2" gutterBottom>
            Selected: {selectedSet.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Indicators ({selectedSet.indicator_names.length}):
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {selectedSet.indicator_names.map((name, idx) => (
              <Chip key={idx} label={name} size="small" color="primary" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default IndicatorSetPicker;
